/**
 * Design tokens for Brig: Naval Tactics.
 *
 * Mid-19th-century Admiralty chart & copper engraving, hand-tinted with watercolour.
 * Plain values only (no CSS-in-JS), so they can be consumed by vanilla-extract,
 * by the Keycloak theme, and by build scripts that bake colours into e-mail templates.
 */

export const palette = {
  // Paper
  paper: '#F1E6C8',
  paperDeep: '#E8D7AA',
  paperShade: '#D9C48F',
  foxing: '#B98B4E',

  // Ink & accents
  ink: '#2F2418',
  inkSoft: '#5A4632',
  sealRed: '#8B2E22',
  sealRedDeep: '#6E2219',
  gold: '#A8863F',
  goldDeep: '#7C6128',
  fadedBlue: '#4A6378',

  // The stern ocean
  sea: '#2E3F4A',
  seaLight: '#3A5260',
  seaDeep: '#24323B',
  seaHatch: '#4F6A7A',

  // The game board: a light watercolour sea, like the wash on a hand-tinted chart.
  // Ink and dark hulls read on it; page chrome keeps the stern ocean above.
  chartSea: '#A9CDE1',
  chartSeaMargin: '#8EB9D1',
  chartSeaGrid: '#6E9BB6',

  // Hand-tinted coast
  ochre: '#C9A25E',
  olive: '#8C8A4F',
  paleGreen: '#B7C29A',
  reef: '#8B2E22',

  // Fleet colours (neutral, no national symbols)
  fleetYellow: '#D8B23A',
  fleetGreen: '#4F7A4A',

  // Feedback
  success: '#3F6B3A',
  danger: '#8B2E22',
} as const;

export const fonts = {
  display: "'Playfair Display', 'Bodoni 72', Didot, 'Times New Roman', serif",
  /** Labels and captions: the body face, in its small size. */
  displayCaps: "'PT Serif', Literata, Georgia, 'Times New Roman', serif",
  body: "'PT Serif', Literata, Georgia, 'Times New Roman', serif",
} as const;

/**
 * Three sizes for three fonts (docs/ux.md U-TYPE-01): small, body and heading. The other keys
 * are kept as aliases so a style's intent still reads, but they resolve to one of the three.
 */
export const fontSizes = {
  xs: '0.875rem',
  sm: '0.875rem',
  md: '1rem',
  lg: '1rem',
  xl: '2rem',
  xxl: '2rem',
  hero: '2rem',
} as const;

export const space = {
  0: '0',
  1: '0.25rem',
  2: '0.5rem',
  3: '0.75rem',
  4: '1rem',
  5: '1.5rem',
  6: '2rem',
  7: '3rem',
  8: '4rem',
} as const;

export const radii = {
  none: '0',
  sm: '2px',
  seal: '999px',
} as const;

export const shadows = {
  paper: '0 1px 0 rgba(47, 36, 24, 0.25), 0 10px 30px rgba(10, 16, 20, 0.45)',
  pressed: 'inset 0 2px 4px rgba(0, 0, 0, 0.35)',
  raised: '0 2px 0 rgba(47, 36, 24, 0.45), 0 3px 8px rgba(0, 0, 0, 0.25)',
} as const;

export const letterSpacing = {
  caps: '0.12em',
  wide: '0.2em',
} as const;

export const breakpoints = {
  tablet: 640,
  desktop: 1024,
} as const;

export const media = {
  tablet: `screen and (min-width: ${breakpoints.tablet}px)`,
  desktop: `screen and (min-width: ${breakpoints.desktop}px)`,
  reducedMotion: '(prefers-reduced-motion: reduce)',
} as const;

export const tokens = {
  color: palette,
  font: fonts,
  fontSize: fontSizes,
  space,
  radius: radii,
  shadow: shadows,
  letterSpacing,
} as const;

export type Tokens = typeof tokens;
